import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import UnifiedMenu from '../components/UnifiedMenu';
import { Users, RefreshCw, Clock, Filter } from 'lucide-react';
import { HOFFMAN_CATEGORIES } from '../hoffmanModel';

const FILTER_REGIONS = [
  { value: 'global', label: 'Global' },
  { value: 'North America', label: 'N. America' },
  { value: 'Europe', label: 'Europe' },
  { value: 'Middle East', label: 'Middle East' },
  { value: 'South Asia', label: 'S. Asia' },
  { value: 'East Asia', label: 'E. Asia' },
  { value: 'Latin America', label: 'Lat. America' },
  { value: 'Africa', label: 'Africa' },
  { value: 'Oceania', label: 'Oceania' },
];

const COUNTRIES_BY_REGION = {
  'global': [],
  'North America': ['United States', 'Canada', 'Mexico'],
  'Europe': ['United Kingdom', 'France', 'Germany', 'Spain', 'Italy', 'Netherlands', 'Sweden', 'Switzerland'],
  'Middle East': ['UAE', 'Saudi Arabia', 'Qatar', 'Bahrain', 'Turkey', 'Egypt'],
  'South Asia': ['India', 'Pakistan', 'Bangladesh', 'Sri Lanka'],
  'East Asia': ['China', 'Japan', 'South Korea', 'Singapore', 'Thailand', 'Malaysia', 'Indonesia', 'Philippines'],
  'Latin America': ['Brazil', 'Argentina', 'Colombia', 'Mexico'],
  'Africa': ['Nigeria', 'South Africa', 'Kenya'],
  'Oceania': ['Australia', 'New Zealand'],
};

export default function CommunityEnhanced({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [region, setRegion] = useState('global');
  const [country, setCountry] = useState('');
  const timerRef = useRef(null);

  const fetchData = async () => {
    try {
      let url = `${API}/sentiment/community?region=${encodeURIComponent(region)}`;
      if (country) url += `&country=${encodeURIComponent(country)}`;
      const res = await axios.get(url);
      setData(res.data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Failed to fetch community data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    timerRef.current = setInterval(fetchData, 15 * 60 * 1000);
    return () => clearInterval(timerRef.current);
  }, [region, country]);

  const getBarColor = (category) => {
    const cat = HOFFMAN_CATEGORIES[category];
    return cat?.color || '#3A5A40';
  };

  const maxPct = data?.categories ? Math.max(...data.categories.map(c => c.percentage)) : 100;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-white via-stone-50 to-stone-100 overflow-hidden">
      <UnifiedMenu active="community" stats={null} currentQuote="" recentMoods={[]} onLogout={onLogout} />

      <div className="flex-none px-4 pt-4 pb-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>
              Community Insights
            </h1>
            <p className="text-xs text-warm-charcoal/50">How the world is feeling right now</p>
          </div>
          <div className="flex items-center gap-2">
            {lastUpdate && (
              <span className="text-[10px] text-warm-charcoal/40 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button
              onClick={() => { setLoading(true); fetchData(); }}
              className="p-1.5 rounded-lg hover:bg-stone-100 transition-colors"
              style={{ touchAction: 'manipulation' }}
              data-testid="community-refresh"
            >
              <RefreshCw className={`w-4 h-4 text-warm-charcoal/50 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {data && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-hunter-green/5 border border-hunter-green/15">
            <Users className="w-4 h-4 text-hunter-green" />
            <p className="text-xs text-warm-charcoal">
              <span className="font-bold text-hunter-green">{data.total_tracking.toLocaleString()}</span> emotional check-ins tracked {country ? `in ${country}` : region === 'global' ? 'globally' : `in ${region}`}
            </p>
          </div>
        )}

        {/* Region Filter */}
        <div className="mt-3 flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-warm-charcoal/40" />
          <div className="flex gap-1.5 flex-wrap">
            {FILTER_REGIONS.map(r => (
              <button
                key={r.value}
                onClick={() => { setRegion(r.value); setCountry(''); setLoading(true); }}
                className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors ${
                  region === r.value
                    ? 'bg-hunter-green text-white'
                    : 'bg-stone-100 text-warm-charcoal/60 hover:bg-stone-200'
                }`}
                style={{ touchAction: 'manipulation' }}
                data-testid={`filter-${r.value}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Country filter */}
        {region !== 'global' && COUNTRIES_BY_REGION[region]?.length > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <select
              value={country}
              onChange={(e) => { setCountry(e.target.value); setLoading(true); }}
              className="flex-1 px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-xs focus:border-hunter-green/50 focus:ring-1 focus:ring-hunter-green/20"
              data-testid="country-filter"
            >
              <option value="">All countries in {region}</option>
              {COUNTRIES_BY_REGION[region].map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Scrollable emotion bars */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {loading && !data ? (
          <div className="flex items-center justify-center h-40">
            <RefreshCw className="w-6 h-6 animate-spin text-hunter-green" />
          </div>
        ) : data?.categories ? (
          <div className="space-y-2 mt-2">
            <p className="text-[10px] text-warm-charcoal/40 uppercase tracking-wider font-semibold mb-1">
              All 18 Emotion Categories — Live Distribution
            </p>
            {data.categories.map((cat, idx) => {
              const color = getBarColor(cat.category);
              const barWidth = Math.max(4, (cat.percentage / maxPct) * 100);
              return (
                <div key={cat.category} className="flex items-center gap-3" data-testid={`community-cat-${idx}`}>
                  <div className="w-28 flex-shrink-0 text-right">
                    <p className="text-[11px] font-medium text-warm-charcoal truncate">{cat.category}</p>
                  </div>
                  <div className="flex-1 h-6 bg-stone-100 rounded-full overflow-hidden relative">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${barWidth}%`, backgroundColor: color, opacity: 0.8 }}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-warm-charcoal/70">
                      {cat.percentage}%
                    </span>
                  </div>
                  <div className="w-14 flex-shrink-0 text-right">
                    <span className="text-[10px] text-warm-charcoal/40">{cat.count.toLocaleString()}</span>
                  </div>
                </div>
              );
            })}

            <div className="mt-4 p-3 rounded-xl bg-stone-50 border border-stone-200">
              <p className="text-[10px] text-warm-charcoal/50 text-center">
                Data refreshes every 15 minutes. Combines global emotional wellness surveys with Mind My Mojo community data.
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-warm-charcoal/50 text-center mt-8">No community data available yet.</p>
        )}
      </div>
    </div>
  );
}
